import { useEffect, useState } from "react";
import LoadingCircle from "../utility/LoadingCircle";
import PDFMerger from "pdf-merger-js";
import { PDFDocument, rgb, StandardFonts, PDFName, PDFHexString, PDFNumber } from "pdf-lib";

async function inputToUint8Array(input) {
	if (input instanceof Uint8Array) return input;
	if (input instanceof ArrayBuffer) return new Uint8Array(input);
	if (typeof Blob !== "undefined" && input instanceof Blob) return new Uint8Array(await input.arrayBuffer());
	if (typeof input === "string") {
		const res = await fetch(input);
		return new Uint8Array(await res.arrayBuffer());
	}
	throw new Error("Unsupported PDF merge input");
}

function addGoToPageLink(pdfDoc, page, rect, targetPage, view = {}) {
	const link = pdfDoc.context.register(
		pdfDoc.context.obj({
			Type: "Annot",
			Subtype: "Link",
			Rect: rect,
			Border: [0, 0, 0],
			C: [0, 0, 1],
			A: {
				S: "GoTo",
				D: [targetPage.ref, "XYZ", view.x != null ? view.x : null, view.y != null ? view.y : null, view.zoom != null ? view.zoom : null],
			},
		}),
	);
	page.node.addAnnot(link);
}

/** Build the PDF reader bookmarks/outline sidebar from the same TOC tree. */
function addPdfOutlineBookmarks(pdfDoc, nodes) {
	if (!nodes || nodes.length === 0) return;
	const ctx = pdfDoc.context;
	const outlinesDict = ctx.obj({ Type: "Outlines" });
	const outlinesRef = ctx.register(outlinesDict);

	const createItems = (itemNodes, parentRef) => {
		const created = [];
		for (let i = 0; i < itemNodes.length; i++) {
			const node = itemNodes[i];
			if (node.targetPageIndex == null || node.targetPageIndex < 0 || node.targetPageIndex >= pdfDoc.getPageCount()) continue;
			const page = pdfDoc.getPage(node.targetPageIndex);
			const dict = ctx.obj({
				Title: PDFHexString.fromText(String(node.title || "Section")),
				Parent: parentRef,
				Dest: [page.ref, "XYZ", node.x != null ? node.x : null, node.y != null ? node.y : null, null],
			});
			const ref = ctx.register(dict);
			const entry = { ref, dict, node };
			created.push(entry);

			const children = Array.isArray(node.children) ? node.children : [];
			if (children.length > 0) {
				const childItems = createItems(children, ref);
				if (childItems.length > 0) {
					dict.set(PDFName.of("First"), childItems[0].ref);
					dict.set(PDFName.of("Last"), childItems[childItems.length - 1].ref);
					dict.set(PDFName.of("Count"), PDFNumber.of(childItems.length));
				}
			}
		}
		for (let i = 0; i < created.length; i++) {
			if (i > 0) created[i].dict.set(PDFName.of("Prev"), created[i - 1].ref);
			if (i < created.length - 1) created[i].dict.set(PDFName.of("Next"), created[i + 1].ref);
		}
		return created;
	};

	const top = createItems(nodes, outlinesRef);
	if (top.length === 0) return;

	let totalCount = 0;
	const walk = (list) => {
		for (let i = 0; i < list.length; i++) {
			totalCount += 1;
			if (list[i].children && list[i].children.length) walk(list[i].children);
		}
	};
	walk(nodes);

	outlinesDict.set(PDFName.of("First"), top[0].ref);
	outlinesDict.set(PDFName.of("Last"), top[top.length - 1].ref);
	outlinesDict.set(PDFName.of("Count"), PDFNumber.of(totalCount));
	pdfDoc.catalog.set(PDFName.of("Outlines"), outlinesRef);
}

function parseTocEntriesHeader(response) {
	const raw = response.headers.get("X-Report-Toc-Entries");
	if (!raw) return [];
	try {
		const json = typeof atob === "function" ? atob(raw) : Buffer.from(raw, "base64").toString("utf8");
		const parsed = JSON.parse(json);
		return Array.isArray(parsed) ? parsed : [];
	} catch (err) {
		return [];
	}
}

function removeAutoNums(input) {
	if (input == null) return "";
	const parts = String(input).split(" - ");
	if (!isNaN(parts[parts.length - 1])) {
		parts.pop();
	}
	return parts.join(" - ");
}

// Match report body defaults from createDefaultDocDefinition / DEFAULT_PDF_SETTINGS
const TOC_STYLE = {
	pageWidth: 595.28,
	pageHeight: 841.89,
	margin: 10,
	footerHeight: 19,
	titleSize: 18,
	groupSize: 16,
	entrySize: 14,
	groupGapBefore: 8,
	entryGap: 18,
	/** Space above the rule under "Table of Contents" */
	titleRuleGapAbove: 10,
	titleRuleThickness: 1.5,
	/** Space below the rule before the first TOC entry / project title */
	titleRuleGapBelow: 8,
};

function countCombinedTocPages(tocItems) {
	const { pageHeight, margin, footerHeight, titleSize, titleRuleGapAbove, titleRuleGapBelow, groupGapBefore, entryGap, groupSize } = TOC_STYLE;
	const titleBlock = titleSize + titleRuleGapAbove + titleRuleGapBelow;
	const linesPerPageBudget = pageHeight - margin - footerHeight - titleBlock;
	let used = 0;
	let pages = 1;
	for (let i = 0; i < tocItems.length; i++) {
		const isFirstOnPage = used === 0;
		const need = tocItems[i].type === "group" ? (isFirstOnPage ? 0 : groupGapBefore) + groupSize + 4 : entryGap;
		if (used + need > linesPerPageBudget) {
			pages += 1;
			used = 0;
			const needAfterBreak = tocItems[i].type === "group" ? groupSize + 4 : entryGap;
			used += needAfterBreak;
			continue;
		}
		used += need;
	}
	return Math.max(1, pages);
}

function drawTocLeaderLine(page, font, title, pageLabel, x, textY, maxTitleWidth, size, pageWidth, margin) {
	const pageLabelWidth = font.widthOfTextAtSize(pageLabel, size);
	const pageLabelX = pageWidth - margin - pageLabelWidth;
	let displayTitle = title;
	let titleWidth = font.widthOfTextAtSize(displayTitle, size);
	while (titleWidth > maxTitleWidth && displayTitle.length > 3) {
		displayTitle = displayTitle.slice(0, -2) + "…";
		titleWidth = font.widthOfTextAtSize(displayTitle, size);
	}

	page.drawText(displayTitle, { x, y: textY, size, font, color: rgb(0, 0, 0) });
	page.drawText(pageLabel, { x: pageLabelX, y: textY, size, font, color: rgb(0, 0, 0) });

	const dotsStart = x + titleWidth + 6;
	const dotsEnd = pageLabelX - 6;
	if (dotsEnd > dotsStart) {
		const dot = ".";
		const dotWidth = font.widthOfTextAtSize(dot, size);
		let dx = dotsStart;
		while (dx + dotWidth <= dotsEnd) {
			page.drawText(dot, { x: dx, y: textY, size, font, color: rgb(0.45, 0.45, 0.45) });
			dx += dotWidth + 1.5;
		}
	}
}

/**
 * Merge cover + parts with pdf-merger-js, then insert a single combined TOC after the cover
 * with project/group separators and header-accurate jump targets.
 */
async function mergeReportWithSingleToc(files, tocEntriesByFile, groupLabelByFile) {
	if (!files || files.length === 0) {
		throw new Error("No PDF files to merge");
	}

	const fileOffsets = [];
	let offset = 0;
	for (let f = 0; f < files.length; f++) {
		const bytes = await inputToUint8Array(files[f]);
		const srcDoc = await PDFDocument.load(bytes);
		fileOffsets.push(offset);
		offset += srcDoc.getPageCount();
	}
	const coverPageCount = (await PDFDocument.load(await inputToUint8Array(files[0]))).getPageCount();

	const tocItems = [];
	let lastGroup = null;
	for (let i = 1; i < files.length; i++) {
		const file = files[i];
		const group = groupLabelByFile.get(file);
		if (group && group !== lastGroup) {
			tocItems.push({ type: "group", title: removeAutoNums(group) });
			lastGroup = group;
		}
		const entries = tocEntriesByFile.get(file) || [];
		for (let e = 0; e < entries.length; e++) {
			const entry = entries[e];
			if (!entry || !entry.page) continue;
			tocItems.push({
				type: "section",
				title: removeAutoNums(entry.title || "Section"),
				absPage0: fileOffsets[i] + (entry.page - 1),
				x: entry.x != null ? entry.x : null,
				y: entry.y != null ? entry.y : null,
			});
		}
	}

	const merger = new PDFMerger();
	for (let f = 0; f < files.length; f++) {
		await merger.add(files[f]);
	}
	const mergedBytes = await merger.saveAsBuffer();
	if (tocItems.length === 0) {
		return mergedBytes;
	}

	const tocPageCount = countCombinedTocPages(tocItems);
	const baseDoc = await PDFDocument.load(mergedBytes);
	const out = await PDFDocument.create();
	const copied = await out.copyPages(baseDoc, baseDoc.getPageIndices());

	for (let i = 0; i < coverPageCount && i < copied.length; i++) {
		out.addPage(copied[i]);
	}

	const font = await out.embedFont(StandardFonts.TimesRoman);
	const fontBold = await out.embedFont(StandardFonts.TimesRomanBold);
	const {
		pageWidth,
		pageHeight,
		margin,
		footerHeight,
		titleSize,
		groupSize,
		entrySize,
		groupGapBefore,
		entryGap,
		titleRuleGapAbove,
		titleRuleThickness,
		titleRuleGapBelow,
	} = TOC_STYLE;
	const linksToAdd = [];
	const outlineNodes = [];
	let currentOutlineGroup = null;
	const contentBottom = margin + footerHeight;

	let tocPage = out.addPage([pageWidth, pageHeight]);
	let y = pageHeight - margin;
	tocPage.drawText("Table of Contents", { x: margin, y: y - titleSize, size: titleSize, font: fontBold, color: rgb(0, 0, 0) });
	y -= titleSize + titleRuleGapAbove;
	tocPage.drawLine({
		start: { x: margin, y },
		end: { x: pageWidth - margin, y },
		thickness: titleRuleThickness,
		color: rgb(0, 0, 0),
	});
	y -= titleRuleGapBelow;

	const ensureSpace = (need) => {
		if (y - need < contentBottom) {
			tocPage = out.addPage([pageWidth, pageHeight]);
			y = pageHeight - margin;
			return true;
		}
		return false;
	};

	for (let i = 0; i < tocItems.length; i++) {
		const item = tocItems[i];
		if (item.type === "group") {
			const startedNewPage = ensureSpace(groupGapBefore + groupSize + 4);
			if (!startedNewPage && i > 0) {
				y -= groupGapBefore;
			}
			tocPage.drawText(String(item.title), { x: margin, y: y - groupSize, size: groupSize, font: fontBold, color: rgb(0, 0, 0) });
			y -= groupSize + 4;
			currentOutlineGroup = { title: String(item.title), children: [], targetPageIndex: null, x: null, y: null };
			outlineNodes.push(currentOutlineGroup);
			continue;
		}

		ensureSpace(entryGap);
		const finalPage0 = item.absPage0 + tocPageCount;
		// Cover is unnumbered; displayed page numbers start at 1 after the cover.
		const pageLabel = String(finalPage0 - coverPageCount + 1);
		const textY = y - entrySize;
		const maxTitleWidth = pageWidth - margin * 2 - 70;
		drawTocLeaderLine(tocPage, font, String(item.title), pageLabel, margin, textY, maxTitleWidth, entrySize, pageWidth, margin);
		linksToAdd.push({
			pageIndex: out.getPageCount() - 1,
			rect: [margin, textY - 2, pageWidth - margin, textY + entrySize + 2],
			targetPageIndex: finalPage0,
			x: item.x,
			y: item.y,
		});
		const outlineEntry = {
			title: String(item.title),
			targetPageIndex: finalPage0,
			x: item.x,
			y: item.y,
		};
		if (currentOutlineGroup) {
			if (currentOutlineGroup.targetPageIndex == null) {
				currentOutlineGroup.targetPageIndex = finalPage0;
				currentOutlineGroup.x = item.x;
				currentOutlineGroup.y = item.y;
			}
			currentOutlineGroup.children.push(outlineEntry);
		} else {
			outlineNodes.push(outlineEntry);
		}
		y -= entryGap;
	}

	for (let i = coverPageCount; i < copied.length; i++) {
		out.addPage(copied[i]);
	}

	const totalPages = out.getPageCount();
	const numberedPages = Math.max(0, totalPages - coverPageCount);
	const footerY = 8;
	const today = new Date();
	const dateString = `Report Created: ${String(today.getMonth() + 1).padStart(2, "0")}/${String(today.getDate()).padStart(2, "0")}/${today.getFullYear()}`;
	for (let i = coverPageCount; i < totalPages; i++) {
		const page = out.getPage(i);
		const label = `Page ${i - coverPageCount + 1} of ${numberedPages}`;
		page.drawText(label, { x: margin, y: footerY, size: 11, font, color: rgb(0, 0, 0) });
		const dateWidth = font.widthOfTextAtSize(dateString, 11);
		page.drawText(dateString, { x: pageWidth - margin - dateWidth, y: footerY, size: 11, font, color: rgb(0, 0, 0) });
	}

	for (let i = 0; i < linksToAdd.length; i++) {
		const link = linksToAdd[i];
		if (link.pageIndex >= out.getPageCount() || link.targetPageIndex >= out.getPageCount()) continue;
		addGoToPageLink(out, out.getPage(link.pageIndex), link.rect, out.getPage(link.targetPageIndex), {
			x: link.x,
			y: link.y,
		});
	}

	const outlineForBookmarks = outlineNodes.filter((node) => node.targetPageIndex != null || (node.children && node.children.length > 0));
	addPdfOutlineBookmarks(out, outlineForBookmarks);

	return await out.save();
}

const ReportModal = ({
	setShowReportModal,
	reports,
	api,
	odooId,
	token,
	requests,
	selectedOdooModel,
	templateNavMappings,
	relatedOdooRecords,
	projectName,
	loading,
}) => {
	const [selectedReportId, setSelectedReportId] = useState("-1");
	const [saveName, setSaveName] = useState("");
	const [reportName, setReportName] = useState("");
	const [generatingReport, setGeneratingReport] = useState(false);
	const [hidePhotos, setHidePhotos] = useState(false);
	const [hideData, setHideData] = useState(false);
	const [requestIds, setRequestIds] = useState([]);
	const [hasError, setHasError] = useState(false);
	const [formattedOdooRecords, setFormattedOdooRecords] = useState([]);
	const [allTicketIds, setAllTicketIds] = useState([]);
	const [selectedTickets, setSelectedTickets] = useState([]);
	const [missingTickets, setMissingTickets] = useState(false);
	const [includeParentChild, setIncludeParentchild] = useState(false);
	const [fetchMessage, setFetchMessage] = useState("Creating the report");
	const [parentChildRecords, setParentChildRecords] = useState([]);
	const [selectedParentChildRecords, setSelectedParentChildrecords] = useState([]);
	const [missingParentChildRecord, setMissingParentChildRecord] = useState(false);
	const modalTypes = ["start", "parentchild", "tickets"];
	const [showModalType, setShowModalType] = useState("start");

	const closeModal = (e) => {
		e.preventDefault();
		setShowReportModal(false);
		setAllTicketIds([]);
	};

	useEffect(() => {
		if (selectedOdooModel === "helpdesk.ticket") {
			setSelectedTickets([parseInt(odooId)]);
		}
	}, [odooId, selectedOdooModel]);

	useEffect(() => {
		if (!templateNavMappings || !relatedOdooRecords) {
			return;
		}
		let keys = templateNavMappings.map((mapData) => mapData.mapping);
		let tempRecords = [];
		for (let i = 0; i < keys.length; i++) {
			if (Object.keys(relatedOdooRecords).includes(keys[i])) {
				let records = relatedOdooRecords[keys[i]].records;
				let type = relatedOdooRecords[keys[i]].type;
				if (type === "many2one") {
					if (records.length > 0) {
						tempRecords.push({
							odoo_id: records[0],
							name: records[1],
							selectedReport: "",
							model: relatedOdooRecords[keys[i]].model,
						});
					}
				} else if (type === "one2many") {
					if (records.length > 0) {
						for (let j = 0; j < records.length; j++) {
							tempRecords.push({
								odoo_id: records[j].id,
								name: records[j].name,
								selectedReport: "",
								model: relatedOdooRecords[keys[i]].model,
							});
						}
					}
				}
			}
		}
		setFormattedOdooRecords(tempRecords);
	}, [templateNavMappings, relatedOdooRecords]);

	useEffect(() => {
		if (reports.length > 0 && selectedReportId === "-1") {
			setSelectedReportId(reports[0].id);
			setSaveName(reports[0].save_name);
			setReportName(reports[0].name);
		}
	}, [reports, selectedReportId]);

	const changeSelectedTemplate = (e, theId) => {
		e.preventDefault();
		if (selectedReportId !== theId) {
			for (let i = 0; i < reports.length; i++) {
				if (`${reports[i].id}` === `${theId}`) {
					setReportName(reports[i].name);
					setSaveName(reports[i].save_name);
					setSelectedReportId(theId);
				}
			}
		}
	};

	const generateCover = async (model) => {
		try {
			const response = await fetch(api + "/report/generate-cover/" + odooId, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + token,
				},
				body: JSON.stringify({ reportId: selectedReportId, hideData, hidePhotos, requestIds, odoo_model: model }),
				mode: "cors",
			});

			const blobData = await response.blob();

			const coverBlob = new Blob([blobData], {
				type: "application/octet-stream",
			});
			const fileURL = window.URL.createObjectURL(coverBlob, { type: "application/octet-stream" });
			return fileURL;
		} catch (err) {
			throw new Error();
		}
	};

	const generatePartsByModel = async (model, recordId, reportIdToUse) => {
		try {
			const response2 = await fetch(api + "/report/generate/" + recordId, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: "Bearer " + token,
				},
				body: JSON.stringify({
					reportId: reportIdToUse,
					hideData,
					hidePhotos,
					requestIds,
					filterModel: model,
					includeToc: false,
				}),
				mode: "cors",
			});

			const tocEntries = parseTocEntriesHeader(response2);
			const blobData2 = await response2.blob();

			const actualPDF = new Blob([blobData2], {
				type: "application/octet-stream",
			});
			const fileURL2 = window.URL.createObjectURL(actualPDF, { type: "application/octet-stream" });
			return { statusCode: response2.status, file: fileURL2, tocEntries };
		} catch (err) {
			throw new Error();
		}
	};

	async function createEmptyPdf() {
		const pdfDoc = await PDFDocument.create();
		const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
		const page = pdfDoc.addPage();
		const { width, height } = page.getSize();
		const fontSize = 24;
		const text = "This project doesn't have any data for this report template.";
		page.drawText(text, {
			x: 10,
			y: height - 30,
			size: fontSize,
			font: timesRomanFont,
			color: rgb(0, 0, 0),
		});

		return await pdfDoc.save();
	}

	async function createTitlePage(titleText) {
		const pdfDoc = await PDFDocument.create();
		const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);

		const page = pdfDoc.addPage();
		const { width, height } = page.getSize();

		const fontSize = 24;
		let formattedText = removeAutoNums(titleText);
		const textWidth = timesRomanFont.widthOfTextAtSize(formattedText, fontSize);

		const textX = (width - textWidth) / 2;
		const textY = height * (2 / 3);

		page.drawText(formattedText, {
			x: textX,
			y: textY,
			size: fontSize,
			font: timesRomanFont,
			color: rgb(0, 0, 0),
		});

		return await pdfDoc.save();
	}

	const getTicketAndParentChildData = async (e) => {
		if (generatingReport) {
			return;
		}
		setHasError(false);
		setGeneratingReport(true);
		try {
			const response = await fetch(
				api + "/report/models/" + odooId + "?reportid=" + selectedReportId + "&model=" + selectedOdooModel + `&hierarchy=${includeParentChild ? 1 : 0}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
					mode: "cors",
				},
			);

			let requestBody = await response.json();
			const { ticketIds, parentInstallation, childInstallations } = requestBody;
			console.log(requestBody);
			setGeneratingReport(false);
			let newParentChildRecords = [];

			if (includeParentChild) {
				if (parentInstallation && parentInstallation !== -1) {
					newParentChildRecords.push({ id: parentInstallation[0], name: parentInstallation[1], isParent: true });
				}
				if (childInstallations && childInstallations.length > 0) {
					for (let i = 0; i < childInstallations.length; i++) {
						newParentChildRecords.push({ id: childInstallations[i].id, name: childInstallations[i].name, isParent: false });
					}
				}
				setParentChildRecords(newParentChildRecords);
			}

			if (ticketIds && ticketIds.length > 0) {
				if (ticketIds.length === 1) {
					setAllTicketIds(ticketIds);
					if (!includeParentChild) {
						generateReport(ticketIds);
					} else {
						setShowModalType("parentchild");
					}
				} else {
					setAllTicketIds([...new Set(ticketIds)]);
					if (includeParentChild) {
						setShowModalType("parentchild");
					} else {
						setShowModalType("tickets");
					}
				}
			} else {
				setAllTicketIds([]);
				if (!includeParentChild) {
					generateReport([]);
				} else {
					setShowModalType("parentchild");
				}
			}
		} catch (err) {
			setGeneratingReport(false);
			setHasError(true);
		}
	};

	const generateReport = async (ticketIds) => {
		if (allTicketIds.length > 1 && selectedTickets.length === 0) {
			setMissingTickets(true);
			return;
		}
		setMissingTickets(false);
		if (generatingReport) {
			return;
		}
		setHasError(false);
		setGeneratingReport(true);
		try {
			console.log("here");
			console.log(odooId);
			console.log(selectedOdooModel);
			const response4 = await fetch(
				api + "/report/models/" + odooId + "?reportid=" + selectedReportId + "&model=" + selectedOdooModel + `&hierarchy=${includeParentChild ? 1 : 0}`,
				{
					method: "GET",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						Authorization: "Bearer " + token,
					},
					mode: "cors",
				},
			);
			let results = await response4.json();
			console.log(results);
			let {
				installationId: originalInstallation,
				roofingId,
				krummyCaptureId,
				parentInstallation: originalParent,
				childInstallations: originalChildren,
			} = results;

			let files = [await generateCover(selectedOdooModel)];
			let filesByCode = {
				204: [],
				200: [],
				400: [],
			};
			const tocEntriesByFile = new Map();
			const groupLabelByFile = new Map();
			let pendingGroupLabel = null;

			const pushTitlePage = (arr, titleBytes, label) => {
				pendingGroupLabel = label || null;
				arr.push(titleBytes);
			};
			const pushGeneratedPart = (statusCode, result) => {
				if (!result || !result.file) return;
				filesByCode[statusCode].push(result.file);
				if (statusCode === 200) {
					tocEntriesByFile.set(result.file, result.tocEntries || []);
					if (pendingGroupLabel) {
						groupLabelByFile.set(result.file, pendingGroupLabel);
					}
				}
			};

			if (includeParentChild) {
				if (originalParent && originalParent !== -1) {
					if (selectedParentChildRecords.includes(originalParent[0]) === false) {
					} else {
						const response4 = await fetch(
							api + "/report/models/" + originalParent[0] + "?reportid=" + selectedReportId + "&model=project.task&hierarchy=0",
							{
								method: "GET",
								headers: {
									"Content-Type": "application/json",
									Accept: "application/json",
									Authorization: "Bearer " + token,
								},
								mode: "cors",
							},
						);

						let results = await response4.json();
						let { installationId, roofingId, krummyCaptureId } = results;
						let addedPage = false;
						if (installationId !== -1) {
							let { statusCode, file, tocEntries } = await generatePartsByModel("project.task", installationId, selectedReportId);
							if (statusCode === 200 && addedPage === false) {
								let parentPage = await createTitlePage(originalParent[1]);
								pushTitlePage(filesByCode[200], parentPage, originalParent[1]);
								addedPage = true;
							}
							pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
						}
						if (krummyCaptureId !== -1) {
							let { statusCode, file, tocEntries } = await generatePartsByModel("x_krummy_capture", krummyCaptureId, selectedReportId);
							if (statusCode === 200 && addedPage === false) {
								let parentPage = await createTitlePage(originalParent[1]);
								pushTitlePage(filesByCode[200], parentPage, originalParent[1]);
								addedPage = true;
							}
							pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
						}
						if (roofingId !== -1) {
							let { statusCode, file, tocEntries } = await generatePartsByModel("x_roofing", roofingId, selectedReportId);
							if (statusCode === 200 && addedPage === false) {
								let parentPage = await createTitlePage(originalParent[1]);
								pushTitlePage(filesByCode[200], parentPage, originalParent[1]);
								addedPage = true;
							}
							pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
						}

						if (formattedOdooRecords.length > 0) {
							for (let i = 0; i < formattedOdooRecords.length; i++) {
								if (formattedOdooRecords[i].selectedReport !== "") {
									let { statusCode, file, tocEntries } = await generatePartsByModel(
										formattedOdooRecords[i].model,
										formattedOdooRecords[i].odoo_id,
										formattedOdooRecords[i].selectedReport,
									);
									pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
								}
							}
						}
					}
				} else if (originalInstallation !== -1) {
					if (selectedParentChildRecords.includes(originalInstallation) === false) {
					} else {
						let parentPage = await createTitlePage(projectName);
						pushTitlePage(filesByCode[200], parentPage, projectName);
						const response4 = await fetch(
							api + "/report/models/" + originalInstallation + "?reportid=" + selectedReportId + "&model=project.task&hierarchy=0",
							{
								method: "GET",
								headers: {
									"Content-Type": "application/json",
									Accept: "application/json",
									Authorization: "Bearer " + token,
								},
								mode: "cors",
							},
						);

						let results = await response4.json();
						let { installationId, roofingId, krummyCaptureId } = results;
						if (installationId !== -1) {
							let { statusCode, file, tocEntries } = await generatePartsByModel("project.task", installationId, selectedReportId);
							pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
						}
						if (krummyCaptureId !== -1) {
							let { statusCode, file, tocEntries } = await generatePartsByModel("x_krummy_capture", krummyCaptureId, selectedReportId);
							pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
						}
						if (roofingId !== -1) {
							let { statusCode, file, tocEntries } = await generatePartsByModel("x_roofing", roofingId, selectedReportId);
							pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
						}

						if (formattedOdooRecords.length > 0) {
							for (let i = 0; i < formattedOdooRecords.length; i++) {
								if (formattedOdooRecords[i].selectedReport !== "") {
									let { statusCode, file, tocEntries } = await generatePartsByModel(
										formattedOdooRecords[i].model,
										formattedOdooRecords[i].odoo_id,
										formattedOdooRecords[i].selectedReport,
									);
									pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
								}
							}
						}
					}
				}
				for (let i = 0; i < originalChildren.length; i++) {
					if (selectedParentChildRecords.includes(originalChildren[i].id) === false) {
						continue;
					}

					const response4 = await fetch(
						api + "/report/models/" + originalChildren[i].id + "?reportid=" + selectedReportId + "&model=project.task&hierarchy=0",
						{
							method: "GET",
							headers: {
								"Content-Type": "application/json",
								Accept: "application/json",
								Authorization: "Bearer " + token,
							},
							mode: "cors",
						},
					);

					let results = await response4.json();
					let { installationId, roofingId, krummyCaptureId } = results;
					let addedPage = false;
					if (installationId !== -1) {
						let { statusCode, file, tocEntries } = await generatePartsByModel("project.task", installationId, selectedReportId);
						if (statusCode === 200 && addedPage === false) {
							let childPage = await createTitlePage(originalChildren[i].name);
							pushTitlePage(filesByCode[200], childPage, originalChildren[i].name);
							addedPage = true;
						}
						pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
					}
					if (krummyCaptureId !== -1) {
						let { statusCode, file, tocEntries } = await generatePartsByModel("x_krummy_capture", krummyCaptureId, selectedReportId);
						if (statusCode === 200 && addedPage === false) {
							let childPage = await createTitlePage(originalChildren[i].name);
							pushTitlePage(filesByCode[200], childPage, originalChildren[i].name);
							addedPage = true;
						}
						pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
					}
					if (roofingId !== -1) {
						let { statusCode, file, tocEntries } = await generatePartsByModel("x_roofing", roofingId, selectedReportId);
						if (statusCode === 200 && addedPage === false) {
							let childPage = await createTitlePage(originalChildren[i].name);
							pushTitlePage(filesByCode[200], childPage, originalChildren[i].name);
							addedPage = true;
						}
						pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
					}

					if (formattedOdooRecords.length > 0) {
						for (let i = 0; i < formattedOdooRecords.length; i++) {
							if (formattedOdooRecords[i].selectedReport !== "") {
								let { statusCode, file, tocEntries } = await generatePartsByModel(
									formattedOdooRecords[i].model,
									formattedOdooRecords[i].odoo_id,
									formattedOdooRecords[i].selectedReport,
								);
								pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
							}
						}
					}
				}

				if (ticketIds.length > 0) {
					let ticketsUsed = 0;
					for (let i = 0; i < ticketIds.length; i++) {
						let { statusCode, file, tocEntries } = await generatePartsByModel("helpdesk.ticket", ticketIds[i], selectedReportId);
						if (ticketsUsed === 0 && statusCode === 200) {
							let ticketPage = await createTitlePage("Tickets");
							pushTitlePage(filesByCode[200], ticketPage, "Tickets");
							ticketsUsed++;
						}
						pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
					}
				}
			} else {
				let titlePage = await createTitlePage(projectName);
				if (originalInstallation !== -1) {
					let { statusCode, file, tocEntries } = await generatePartsByModel("project.task", originalInstallation, selectedReportId);
					if (statusCode !== 400) {
						pushTitlePage(filesByCode[200], titlePage, projectName);
					}
					pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
				}
				if (krummyCaptureId !== -1) {
					let { statusCode, file, tocEntries } = await generatePartsByModel("x_krummy_capture", krummyCaptureId, selectedReportId);
					if (statusCode !== 400) {
						pushTitlePage(filesByCode[200], titlePage, projectName);
					}
					pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
				}
				if (roofingId !== -1) {
					let { statusCode, file, tocEntries } = await generatePartsByModel("x_roofing", roofingId, selectedReportId);
					if (statusCode !== 400) {
						pushTitlePage(filesByCode[200], titlePage, projectName);
					}
					pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
				}
				if (ticketIds.length > 0) {
					let ticketsUsed = 0;
					for (let i = 0; i < ticketIds.length; i++) {
						let { statusCode, file, tocEntries } = await generatePartsByModel("helpdesk.ticket", ticketIds[i], selectedReportId);
						if (ticketsUsed === 0 && statusCode === 200) {
							let ticketPage = await createTitlePage("Tickets");
							pushTitlePage(filesByCode[200], ticketPage, "Tickets");
							ticketsUsed++;
						}
						pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
					}
				}

				if (formattedOdooRecords.length > 0) {
					for (let i = 0; i < formattedOdooRecords.length; i++) {
						if (formattedOdooRecords[i].selectedReport !== "") {
							let { statusCode, file, tocEntries } = await generatePartsByModel(
								formattedOdooRecords[i].model,
								formattedOdooRecords[i].odoo_id,
								formattedOdooRecords[i].selectedReport,
							);
							pushGeneratedPart(statusCode, { statusCode, file, tocEntries });
						}
					}
				}
			}
			if (filesByCode[200].length > 0) {
				files.push(...filesByCode[200]);
			} else if (filesByCode[204].length > 0) {
				files.push(await createEmptyPdf());
			} else if (filesByCode[400].length > 0) {
				files.push(await createEmptyPdf());
			} else {
				throw new Error();
			}

			const mergedPDF = await mergeReportWithSingleToc(files, tocEntriesByFile, groupLabelByFile);
			const pdfBlob = new Blob([mergedPDF], { type: "application/pdf" });
			const url = URL.createObjectURL(pdfBlob);
			const link = document.createElement("a");
			link.download = saveName + ".pdf";
			link.href = url;
			link.click();
			for (const file of files) {
				if (typeof file === "string") URL.revokeObjectURL(file);
			}
			setGeneratingReport(false);
		} catch (err) {
			setGeneratingReport(false);
			setHasError(true);
		}
	};
	const preventPropagation = (e) => {
		e.stopPropagation();
	};
	const changeHidePhotos = (e) => {
		setHidePhotos(e.target.checked);
	};

	const changeHideData = (e) => {
		setHideData(e.target.checked);
	};

	const changeParentChildSelection = (e) => {
		setIncludeParentchild(e.target.checked);
	};

	const toggleRequest = (e, requestId) => {
		if (requestIds.includes(requestId)) {
			setRequestIds((prev) => prev.filter((filterData) => filterData !== requestId));
		} else {
			setRequestIds([...requestIds, requestId]);
		}
	};

	const toggleSelectedTicket = (ticketId) => {
		if (selectedTickets.includes(ticketId)) {
			setSelectedTickets((prev) => [...prev.filter((filterData) => filterData !== ticketId)]);
		} else {
			setSelectedTickets([...selectedTickets, ticketId]);
		}
	};

	const toggleSelectedParentChildRecord = (recordId) => {
		setMissingParentChildRecord(false);
		if (selectedParentChildRecords.includes(recordId)) {
			setSelectedParentChildrecords((prev) => [...prev.filter((filterData) => filterData.toString() !== recordId.toString())]);
		} else {
			setSelectedParentChildrecords([...selectedParentChildRecords, recordId]);
		}
	};

	const validateParentChildSelections = () => {
		if (selectedParentChildRecords.length > 0) {
			setMissingParentChildRecord(false);
			if (allTicketIds.length > 1) {
				setShowModalType("tickets");
			} else {
				generateReport(allTicketIds);
			}
		} else {
			setMissingParentChildRecord(true);
		}
	};

	const processStartCreation = async () => {
		await getTicketAndParentChildData();
	};

	if (parentChildRecords.length > 0 && showModalType !== "tickets") {
		return (
			<div className="text-black fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black/50 z-50" onClick={closeModal}>
				<div className="bg-white w-full lg:w-1/4 m-2 rounded-lg " onClick={preventPropagation}>
					<div className="m-4 flex justify-between items-center">
						<p className="text-3xl">Parent/Child Records</p>
						<div>
							<button type="button" className="rounded-lg bg-gray-500 p-2 text-white " onClick={closeModal}>
								Close
							</button>
						</div>
					</div>
					<div className="mx-4">
						<p>Select the records you want to include.</p>
					</div>
					<div className="m-4 flex flex-col gap-4">
						<form>
							<p>Records:</p>
							{parentChildRecords.map((mapData) => (
								<div key={mapData.id} className="flex gap-1 items-center">
									<input
										id={"parentchild" + mapData.id}
										type="checkbox"
										checked={selectedParentChildRecords.includes(mapData.id)}
										onChange={() => toggleSelectedParentChildRecord(mapData.id)}></input>
									<label htmlFor={"parentchild" + mapData.id}>
										{mapData.name}
										{mapData.isParent ? " (Parent)" : null}
										{mapData.id === odooId ? " (Current)" : null}
									</label>
								</div>
							))}
						</form>
						<div className=" flex flex-col justify-center items-center gap-2">
							{generatingReport ? (
								<div className="flex flex-col justify-center items-center">
									<LoadingCircle />
									<p>Creating the report...</p>
								</div>
							) : selectedReportId !== "-1" ? (
								<div className="flex gap-4 justify-center items-center">
									<button
										className={`bg-gray-500 hover:bg-gray-600 text-white p-3 rounded-lg`}
										onClick={() => {
											setParentChildRecords([]);
											setAllTicketIds([]);
											setSelectedTickets([parseInt(odooId)]);
										}}>
										Go Back
									</button>
									<button
										className={`${
											selectedReportId !== "-1" ? "bg-blue-500 hover:bg-blue-700 " : "bg-gray-500 hover:bg-gray-600 "
										} text-white p-3 rounded-lg`}
										onClick={() => validateParentChildSelections()}>
										{allTicketIds.length > 1 ? "Select Tickets" : "Create Report"}
									</button>
								</div>
							) : (
								<button className={`bg-gray-500 hover:bg-gray-600 text-white p-3 rounded-lg`}>Create Report</button>
							)}
							{missingParentChildRecord ? <p className="text-red-500">Please include at least one record.</p> : null}
							{selectedReportId === "-1" ? <p>Select a Report</p> : null}
							{hasError ? (
								<p className="text-red-500">Something went wrong. Please try again. If this keeps happening, please contact IT.</p>
							) : null}
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (allTicketIds.length > 1 && showModalType === "tickets") {
		return (
			<div className="text-black fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black/50 z-50" onClick={closeModal}>
				<div className="bg-white w-full lg:w-1/4 m-2 rounded-lg " onClick={preventPropagation}>
					<div className="m-4 flex justify-between items-center">
						<p className="text-3xl">Tickets</p>
						<div>
							<button type="button" className="rounded-lg bg-gray-500 p-2 text-white " onClick={closeModal}>
								Close
							</button>
						</div>
					</div>
					<div className="mx-4">
						<p>This project has multiple tickets, select the ones you want to include.</p>
					</div>
					<div className="m-4 flex flex-col gap-4">
						<form>
							<p>Odoo Ids:</p>
							{allTicketIds.map((mapData) => (
								<div key={mapData} className="flex gap-1 items-center">
									<input
										id={"ticket" + mapData}
										type="checkbox"
										checked={selectedTickets.includes(mapData)}
										onChange={() => toggleSelectedTicket(mapData)}></input>
									<label htmlFor={"ticket" + mapData}>
										{mapData}
										{selectedOdooModel === "helpdesk.ticket" && parseInt(odooId) === mapData ? " (Current Project)" : null}
									</label>
								</div>
							))}
						</form>
						<div className=" flex flex-col justify-center items-center gap-2">
							{generatingReport ? (
								<div className="flex flex-col justify-center items-center">
									<LoadingCircle />
									<p>Creating the report...</p>
								</div>
							) : selectedReportId !== "-1" ? (
								<div className="flex gap-4 justify-center items-center">
									<button
										className={`bg-gray-500 hover:bg-gray-600 text-white p-3 rounded-lg`}
										onClick={() => {
											if (includeParentChild) {
												setShowModalType("parentchild");
											} else {
												setAllTicketIds([]);
												setSelectedTickets([parseInt(odooId)]);
											}
										}}>
										Go Back
									</button>
									<button
										className={`${
											selectedReportId !== "-1" ? "bg-blue-500 hover:bg-blue-700 " : "bg-gray-500 hover:bg-gray-600 "
										} text-white p-3 rounded-lg`}
										onClick={() => {
											if (selectedTickets.length === 0) {
												setMissingTickets(true);
											} else {
												setMissingTickets(false);
												generateReport(selectedTickets);
											}
										}}>
										Create Report
									</button>
								</div>
							) : (
								<button className={`bg-gray-500 hover:bg-gray-600 text-white p-3 rounded-lg`}>Create Report</button>
							)}
							{missingTickets ? <p className="text-red-500">Please include at least one ticket.</p> : null}
							{selectedReportId === "-1" ? <p>Select a Report</p> : null}
							{hasError ? (
								<p className="text-red-500">Something went wrong. Please try again. If this keeps happening, please contact IT.</p>
							) : null}
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (!loading && reports && reports.length === 0) {
		return (
			<div className="text-black fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black/50 z-50" onClick={closeModal}>
				<div className="bg-white w-full lg:w-1/4 m-2 rounded-lg " onClick={preventPropagation}>
					<div className="m-4 flex flex-col justify-between items-center">
						<p className="font-bold text-2xl mb-2">Not Found</p>
						<p className="text-red-500">This template is not used in any reports.</p>
					</div>
				</div>
			</div>
		);
	}
	return (
		<div className="text-black fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black/50 z-50" onClick={closeModal}>
			<div className="bg-white w-full lg:w-2/4 m-2 rounded-lg " onClick={preventPropagation}>
				<div className="m-4 flex justify-between items-center">
					<p className="text-3xl">Create Report</p>
					<div>
						<button type="button" className="rounded-lg bg-gray-500 p-2 text-white " onClick={closeModal}>
							Close
						</button>
					</div>
				</div>
				<div className="m-4 flex flex-col gap-4">
					<div>
						<p className="text-lg">Report</p>
						<form className="flex flex-col gap-2">
							<select
								id="templateNames"
								value={selectedReportId}
								className="border border-black rounded-lg p-1 w-full mt-2"
								onChange={(e) => changeSelectedTemplate(e, e.target.value)}>
								{reports && reports.length > 0
									? reports.map((mapData) => {
											return (
												<option key={mapData.id} value={mapData.id}>
													{mapData.name}
												</option>
											);
										})
									: null}
							</select>
						</form>
					</div>
					<form>
						<div className="flex gap-2  items-center">
							<input id="hide-photos" type="checkbox" checked={hidePhotos} onChange={changeHidePhotos}></input>
							<label htmlFor="hide-photos">Hide Photos</label>
						</div>
						<div className="flex gap-2  items-center">
							<input id="hide-data" type="checkbox" checked={hideData} onChange={changeHideData}></input>
							<label htmlFor="hide-data">Hide Data</label>
						</div>
						<div className="flex gap-2  items-center">
							<input id="include-parentchild" type="checkbox" checked={includeParentChild} onChange={changeParentChildSelection}></input>
							<label htmlFor="include-parentchild">Include Parent/Child Records</label>
						</div>
					</form>
					<div className=" flex flex-col justify-center items-center gap-2">
						{generatingReport ? (
							<div className="flex flex-col justify-center items-center">
								<LoadingCircle />
								<p>Creating the report...</p>
							</div>
						) : selectedReportId !== "-1" ? (
							<button
								className={`${
									selectedReportId !== "-1" ? "bg-blue-500 hover:bg-blue-700 " : "bg-gray-500 hover:bg-gray-600 "
								} text-white p-3 rounded-lg`}
								onClick={processStartCreation}>
								{includeParentChild ? "Select Records" : "Create Report"}
							</button>
						) : (
							<button className={`bg-gray-500 hover:bg-gray-600 text-white p-3 rounded-lg`}>Create Report</button>
						)}
						{selectedReportId === "-1" ? <p>Select a Report</p> : null}

						{hasError ? <p className="text-red-500">Something went wrong. Please try again. If this keeps happening, please contact IT.</p> : null}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ReportModal;
